import React from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useMomentsToday } from '../../hooks/useMomentsToday'
import MomentsReveal from '../../components/MomentsReveal'

// MomentsDayPage — /moments/:promptId
//
// Read-only view of a single past day's pair. Reaches the reveal
// view via the URL, not by submitting — used from the archive grid.
// Comments + reactions remain editable since old moments are still
// fair game for "haha I'd forgotten about that" interactions weeks
// later.
export function MomentsDayPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { promptId } = useParams()
  const {
    prompt, mine, partner, partnerName, myImageUrl, partnerImageUrl,
    loading: hookLoading,
  } = useMomentsToday(promptId, { locale })

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || hookLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }
  if (!prompt) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <p dir="auto" className="mb-4 text-base opacity-80">{t('moments.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/moments')}
          className="rounded-full bg-rose-500 px-5 py-2 text-sm font-medium text-white"
        >
          {t('moments.backToIndex')}
        </button>
      </div>
    )
  }

  // Localized header date — falls back to the raw ISO date string if
  // the browser can't parse the locale for some reason.
  let prettyDate = prompt.prompt_date
  try {
    const d = new Date(prompt.prompt_date + 'T00:00:00')
    prettyDate = d.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch { /* keep raw */ }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/moments/archive')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('moments.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 dir="auto" className="text-lg font-semibold">{prettyDate}</h1>
      </div>
      <MomentsReveal
        mine={mine}
        partner={partner}
        myImageUrl={myImageUrl}
        partnerImageUrl={partnerImageUrl}
        partnerName={partnerName}
        windowOpen={false}
        viewerId={user?.id}
        locale={locale}
        aiCaption={prompt?.ai_caption}
        aiCaptionAr={prompt?.ai_caption_ar}
        aiTags={prompt?.ai_tags}
      />
    </div>
  )
}
